// © 2026 AttainIP Engineering
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { fetchAnalyses } from '@/api/queries';
import { Card } from '@/components/Card';
import { useAdminPrefs } from '@/lib/adminPrefs';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'complete', label: 'Complete' },
  { value: 'processing', label: 'Processing' },
  { value: 'error', label: 'Error' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function AnalysesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { hideInternal } = useAdminPrefs();

  const { data, isLoading } = useQuery({
    queryKey: ['analyses', { statusFilter, hideInternal }],
    queryFn: () => fetchAnalyses({
      limit: 200,
      status: statusFilter || undefined,
      hide_internal: hideInternal,
    }),
  });

  const analyses = data?.data || [];
  const total = data?.total || analyses.length;

  const filtered = useMemo(() => {
    if (!search.trim()) return analyses;
    const q = search.toLowerCase();
    return analyses.filter((a: any) =>
      (a.analysis_id || '').toLowerCase().includes(q) ||
      (a.user_email || '').toLowerCase().includes(q) ||
      (a.case_name || '').toLowerCase().includes(q) ||
      (a.status || '').toLowerCase().includes(q)
    );
  }, [analyses, search]);

  if (isLoading) return <p className="text-gray-500">Loading analyses...</p>;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">Analyses ({total})</h1>

      <div className="flex gap-2 mb-4">
        {STATUS_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setStatusFilter(value)}
            className={`px-3 py-1 rounded text-xs font-medium ${
              statusFilter === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ID, user, case name, or status..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">ID</th>
              <th className="pb-2 font-medium">User</th>
              <th className="pb-2 font-medium">Case</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium">Progress</th>
              <th className="pb-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a: any) => (
              <tr key={a.analysis_id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 font-mono text-xs">{a.analysis_id}</td>
                <td className="py-2 text-xs">{a.user_email || '—'}</td>
                <td className="py-2 text-xs text-gray-500 truncate max-w-40">{a.case_name || '—'}</td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    a.status === 'complete' ? 'bg-green-100 text-green-700' :
                    a.status === 'error' ? 'bg-red-100 text-red-700' :
                    a.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {a.status || '—'}
                  </span>
                </td>
                <td className="py-2 text-xs text-gray-400">
                  {a.progress_percent != null ? `${a.progress_percent}%` : '—'}
                  {a.progress_stage && a.progress_stage !== 'pending' && ` (${a.progress_stage})`}
                </td>
                <td className="py-2 text-xs text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-gray-400 text-sm py-4 text-center">
            {search ? `No analyses matching "${search}"` : 'No analyses found'}
          </p>
        )}
      </Card>
    </div>
  );
}
